import React, { createContext, useContext, useEffect, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { socketAPI } from 'app/api';
import { useSelector } from 'react-redux';
import { RootState } from 'types/RootState';

interface SocketContextType {
  socket: Socket | null;
}

const SocketContext = createContext<SocketContextType>({ socket: null });

export const useSocket = () => useContext(SocketContext);
export let socket: Socket | null = null;

export const createSocket = (roomId: string): Socket => {
  socket = io(socketAPI, {
    transports: ['websocket', 'polling', 'flashsocket'],
    secure: true,
    auth: cb => cb({ token: localStorage.getItem('_UFT_') as string }),
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    randomizationFactor: 0.5,
  });

  socket.on('connect', () => {
    if (socket) {
      console.log(`Frontend connected with id: ${socket.id}`);
      socket.emit('join_room', roomId);
    }
  });

  socket.on('disconnect', reason => {
    console.log(`Socket disconnected: ${reason}`);
    if (reason === 'io server disconnect' && socket) {
      // Reconnect manually if the server disconnects the client
      socket.connect();
    }
  });

  return socket;
};

export const SocketContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const state = useSelector((state: RootState) => state?.authPage?.authData);

  useEffect(() => {
    // Don't connect socket on docs pages
    const isDocsPage = window.location.pathname.startsWith('/docs');
    if (isDocsPage) return;

    if (state?.isLoggedIn && state.business?.id) {
      const newSocket = createSocket(state.business.id.toString());
      setSocket(newSocket);
      return () => {
        newSocket.close();
      };
    }
  }, [state?.business?.id, state?.isLoggedIn]);

  return (
    <SocketContext.Provider value={{ socket }}>
      {children}
    </SocketContext.Provider>
  );
};
